<template lang="pug">
  .host
    LoadingSpinnerOverlay(v-if="!layoutComponent")
    component(v-if="host && layoutComponent" :is="layoutComponent" :host="host" :item-id="itemId")
    HostViewLoader(v-else)
    footer(v-if="layoutComponent")
      .container.touridat-hotline.mt-5
        .text-center
          i.d-block Noch Fragen? Wir sind für Sie da:
          a.link(@click="showHotlineModal")
            b.mb-0.txt-tdays-lighter +49 (0) 2974 / 9697-98
            small.txt-td-grey-basic &nbsp; Mo.-Fr.: 9-18 Uhr
</template>

<script>
import { isNumeric } from "@/lib/helper/math";
import { Hosts } from "@/graphql/Hosts.ts";
import { getHostViewComponentByTemplateId } from "@/lib/helper/host";
import LoadingSpinnerOverlay from "@/views/components/loading/LoadingSpinnerOverlay";
import { showHotlineModal } from "@/lib/components/hotlineModal";
import HostViewLoader from "@/views/host/HostViewLoader";
import EventBus from "@/event-bus";
import { WebTemplatesEnum } from "@/utils/WebTemplatesEnum.ts";
import { createError } from "@/lib/components/error/createError";
import FatalError from "@/lib/components/error/FatalError";
import { objectWithout } from "@/lib/helper/object";
import { stripSlashes } from "@/lib/helper/strings";
import { getReroute } from "@/lib/helper/router";
import { useSearchStore } from "@/store/search-store";
import { HostSearch } from "@/graphql/HostSearch.ts";

export default {
  name: "HostView",
  components: {
    LoadingSpinnerOverlay,
    HostViewLoader
  },
  props: {
    name: String
  },
  data() {
    return {
      host: undefined
    };
  },
  computed: {
    itemId() {
      const id = this.$route.hash?.replace("#", "");
      if (!id || !isNumeric(id)) return;
      return id;
    },
    layoutComponent() {
      return getHostViewComponentByTemplateId(this.host?.HostViewTemplateId);
    }
  },
  watch: {
    async itemId(value) {
      if (!this.host.Items.find((itm) => itm.id == value)) { // this host does not have an item matching the hash
        await this.handleUnknownItem();
      }
    }
  },
  methods: {
    showHotlineModal,
    async findReroute() {
      const currentPath = stripSlashes(this.$route?.fullPath);
      return await getReroute(this.$apollo, currentPath);
    },
    async handleEmptyHost() {
      // look if there is a matching deadEndUrl
      const reroute = await this.findReroute();
      if (!reroute) {
        throw createError(
          "Der gesuchte Gastgeber konnte nicht gefunden werden."
        );
      }
      this.$router.replace(reroute);
    },
    async handleUnknownItem() {
      // look if there is a matching deadEndUrl
      const reroute = await this.findReroute();
      if (!reroute) {
        throw createError(
          "Der gesuchte Artkel konnte nicht gefunden werden."
        );
      }
      this.$router.replace(reroute);
    },
    async updateHost() {
      try {
        if (!this.name) return;
        this.host = await this.$apollo
          .query({
            query: Hosts.Queries.HostByName,
            variables: {
              name: this.name
            }
          })
          .then(async (response) => {
            const host = response?.data?.hostByName;
            if (!host?.Items?.length) { // host is undefined or host.Items does not have a length
              return await this.handleEmptyHost();
            }
            host.Items = host.Items.map((item) => {
              // map the host to each item
              return {
                ...item,
                Host: objectWithout(host, ["Items"])
              };
            });

            return host;
          });
        if (!this.itemId) {
          const firstItem = this.host.Items[0];
          if (!firstItem?.id) {
            return await this.handleUnknownItem();
          }
          this.$router.replace({ hash: `#${firstItem.id}` });
        }
      } catch (e) {
        if (e instanceof FatalError) throw e;
        this.$log.error(e);
      }
    }
  },
  async mounted() {
    if (this.name) {
      await this.updateHost();
    }
    EventBus.$emit("changeWebTemplateType", WebTemplatesEnum.Default);
    const store = useSearchStore();
    const lastSearch = store.lastSearch;
    if (lastSearch && (this.itemId && lastSearch.requestId)) {
      await this.$apollo.mutate({
        mutation: HostSearch.Mutations.SelectSearchItem,
        variables: {
          query: lastSearch.query || "",
          itemId: this.itemId,
          requestId: lastSearch.requestId
        }
      });
      store.clearLastSearch();
    }
  }
};
</script>
<style lang="scss">
@import "@/assets/styles/mediaqueries";

.host {
  .menu-column {
    position: absolute;
    height: 100%;
    pointer-events: none;
    display: flex;
    @include lg {
      height: auto;
      position: relative;
      display: block;
    }

    & > .host-sidebar {
      pointer-events: auto;
      position: sticky;
      z-index: 0;
      bottom: 0;
      top: auto;
      align-self: end;
      width: 100%;
      @include lg {
        bottom: auto;
        top: 0;
        margin-top: -90px;
        z-index: 1;
      }
    }
  }

  .details-column {
    padding-bottom: 200px;
  }
}
</style>
